/**
 * Espelho de `backend/src/auth/authz/rank.ts` e `capabilities.ts`. Divergir
 * desta escala/lógica quebra a paridade com o backend — mudou lá, muda aqui
 * também.
 *
 * O `caps` que o backend serve já vem com o teto da empresa aplicado
 * (`effectiveCapsOf` em `auth.service.ts`). Sessão antiga em cache sem `caps`
 * é fail-closed e o `AuthContext` se auto-cura (1 refresh de perfil).
 */

export const RANK = {
  visualizador: 0,
  assistente: 1,
  analista: 2,
  supervisor: 3,
  coordenador: 4,
  gerente: 5,
  diretor: 6,
  presidente: 7,
  desenvolvedor: 8,
  agente: 8,
} as const

export type GlobalTipoCode = keyof typeof RANK

/** Chave livre de capability (vocabulário interno TYPE_SEEDS ou ExternalCapabilities). */
export type CapKey = string

/** true estrito — array/undefined (ex.: allowed_topics) não contam como flag. */
export function capsHas(
  caps: Record<string, unknown> | null | undefined,
  key: CapKey,
): boolean {
  return caps?.[key] === true
}

/** Formato mínimo aceito por effectiveRankOf/effectiveCapsOf. */
export interface AuthzUser {
  rank?: number
  caps?: Record<string, unknown> | null
}

/**
 * Rank efetivo do vínculo. Fail-closed: sem usuário ou sem `rank` → 0
 * (paridade com o `effectiveRankOf` do backend; o tipo de cliente já nasce
 * com rank 0). T12: o fallback por nome de cargo morreu dos dois lados.
 */
export function effectiveRankOf(user: AuthzUser | null | undefined): number {
  return user?.rank ?? 0
}

/**
 * Caps efetivas: `caps` da sessão.
 * Fail-closed: sem nada → `null` (`checkCap` trata `null` como "nenhuma cap").
 */
export function effectiveCapsOf(
  user: AuthzUser | null | undefined,
): Record<string, unknown> | null {
  return user?.caps ?? null
}

export function checkRank(
  user: AuthzUser | null | undefined,
  min: number | GlobalTipoCode,
): boolean {
  if (!user) return false
  const minRank = typeof min === 'number' ? min : RANK[min]
  return effectiveRankOf(user) >= minRank
}

export function checkCap(
  user: AuthzUser | null | undefined,
  key: CapKey | CapKey[],
  mode: 'all' | 'any' = 'all',
): boolean {
  const caps = effectiveCapsOf(user)
  const keys = Array.isArray(key) ? key : [key]
  if (keys.length === 0) return false
  return mode === 'any'
    ? keys.some((k) => capsHas(caps, k))
    : keys.every((k) => capsHas(caps, k))
}

/** `AuthzUser` + os mapas por empresa do vínculo (`caps_by_company`, `rank_by_company`). */
export interface AuthzUserPorEmpresa extends AuthzUser {
  caps_by_company?: Record<string, Record<string, unknown>> | null
  rank_by_company?: Record<string, number> | null
}

/**
 * Permissão é do vínculo (spec 2026-10-19), espelho de `capEm` no backend:
 * a chave vale se o vínculo em ALGUMA das empresas dadas a concede (as duas
 * pernas de um processo: dona e do tipo). `can_manage_all` é o único bypass.
 * Sem mapa (sessão antiga) → false; o AuthContext renova o perfil.
 *
 * Diferença DE PROPÓSITO em relação ao `capEm`: aqui o bypass de
 * `can_manage_all` não exige vínculo na empresa (o backend só o dá dentro das
 * empresas do vínculo). É só UI — o backend decide — e Dev/Presidente agem fora
 * do vínculo via `can_view_all_processes`; apertar deixaria as telas deles
 * read-only. Quem precisa de vínculo de fato (ex.: Narwal) confere
 * `member_company_ids` à parte. Chave de sistema não é tratada aqui (vive só na
 * união, via `hasCap`).
 */
export function checkCapIn(
  user: AuthzUserPorEmpresa | null | undefined,
  empresas: Array<number | string | null | undefined> | number | string | null | undefined,
  key: CapKey,
): boolean {
  if (!user) return false
  if (capsHas(effectiveCapsOf(user), 'can_manage_all')) return true
  const ids = (Array.isArray(empresas) ? empresas : [empresas])
    .map((e) => (e == null ? null : Number(e)))
    .filter((e): e is number => e != null && Number.isFinite(e))
  return ids.some((e) => capsHas(user.caps_by_company?.[String(e)], key))
}

/**
 * Rank é do vínculo (spec 2026-10-20), espelho de `hasRankEm` no backend: o
 * nível vale NA empresa dona do recurso. Irrestrito (`can_view_all_processes`
 * / `can_manage_all`) usa o máximo; sem empresa → false; várias → todas. Sem
 * mapa (sessão antiga) → false; o AuthContext renova o perfil.
 */
export function checkRankIn(
  user: AuthzUserPorEmpresa | null | undefined,
  empresas: Array<number | string | null | undefined> | number | string | null | undefined,
  min: number | GlobalTipoCode,
): boolean {
  if (!user) return false
  const alvo = typeof min === 'number' ? min : RANK[min]
  if (alvo === undefined) return false
  const uniao = effectiveCapsOf(user)
  if (capsHas(uniao, 'can_view_all_processes') || capsHas(uniao, 'can_manage_all')) {
    return effectiveRankOf(user) >= alvo
  }
  const lista = Array.isArray(empresas) ? empresas : [empresas]
  if (lista.length === 0) return false
  // '' / só espaços viram 0 em `Number(...)` (finito): tratar como inválido.
  const ids = lista.map((e) =>
    e == null || (typeof e === 'string' && e.trim() === '') ? NaN : Number(e),
  )
  if (ids.some((e) => !Number.isFinite(e))) return false
  return ids.every((e) => (user.rank_by_company?.[String(e)] ?? 0) >= alvo)
}
